import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Flex, Icon, Wrap, WrapItem, Tag, TagLabel, TagCloseButton, useColorModeValue } from "@chakra-ui/react";
import moment from "moment";
import { MainTable } from "../../../components/Table/MainTable";
import { useAuth0 } from "@auth0/auth0-react";
import { useKnoxDevices } from "../../../lib/hooks";
import { formatEpochDate, displayOrDash } from "../../../utils/knox";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { SidebarContext } from "../../../contexts/SidebarContext";
import FilterSystem, { getActiveFilterCount, getFilterDisplayValue, removeFilter as removeFilterHelper } from "../../../components/filters/FilterSystem";

// Backend sortBy whitelist. imei2/updateTime are not supported; updateTime is aliased
// server-side to modifiedDate but we emit the canonical name.
const SORT_WHITELIST = new Set([
  "modifiedDate",
  "createDate",
  "licenseExpiryDate",
  "imei",
  "status",
  "firmwareVersion",
  "agentVersion",
]);

const dateToEpochStart = (yyyymmdd) =>
  yyyymmdd ? String(moment.utc(yyyymmdd).startOf("day").valueOf()) : "";
const dateToEpochEnd = (yyyymmdd) =>
  yyyymmdd ? String(moment.utc(yyyymmdd).endOf("day").valueOf()) : "";

function KnoxDevices() {
  const { user, isLoading: userLoading } = useAuth0();
  const { sidebarCollapsed } = useContext(SidebarContext);
  const [mounted, setMounted] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [sortBy, setSortBy] = useState("modifiedDate");
  const [sortOrder, setSortOrder] = useState("descending");

  const [filters, setFilters] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("auth0_token");
    if (user && !userLoading && token) setMounted(true);
  }, [user, userLoading]);

  const apiFilters = useMemo(
    () => ({
      status: filters.status || "",
      imeiSearch: filters.imeiSearch || "",
      createDateFrom: dateToEpochStart(filters.createDate_from),
      createDateTo: dateToEpochEnd(filters.createDate_to),
      modifiedDateFrom: dateToEpochStart(filters.modifiedDate_from),
      modifiedDateTo: dateToEpochEnd(filters.modifiedDate_to),
      licenseExpiryFrom: dateToEpochStart(filters.licenseExpiryDate_from),
      licenseExpiryTo: dateToEpochEnd(filters.licenseExpiryDate_to),
    }),
    [filters]
  );

  const { knoxDevices, isKnoxDevicesLoading } = useKnoxDevices(
    mounted,
    page,
    perPage,
    sortBy,
    sortOrder,
    apiFilters
  );

  const items = knoxDevices?.body?.deviceList || [];
  const totalCount = knoxDevices?.body?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  const filterFields = useMemo(
    () => [
      {
        key: "imeiSearch",
        label: "IMEI",
        type: "text",
        placeholder: "Search IMEI or IMEI 2",
      },
      {
        key: "status",
        label: "Status",
        type: "text",
        placeholder: "e.g. Locked, Unlocked",
      },
      {
        key: "createDate",
        label: "Create Date",
        type: "daterange",
      },
      {
        key: "modifiedDate",
        label: "Modified Date",
        type: "daterange",
      },
      {
        key: "licenseExpiryDate",
        label: "License Expiry",
        type: "daterange",
      },
    ],
    []
  );

  const handleHeaderSort = (accessor) => {
    if (!SORT_WHITELIST.has(accessor)) return;
    if (sortBy === accessor) {
      setSortOrder((prev) => (prev === "ascending" ? "descending" : "ascending"));
    } else {
      setSortBy(accessor);
      setSortOrder("ascending");
    }
  };

  const columns = useMemo(
    () => [
      { Header: () => (<Flex align="center" onClick={() => handleHeaderSort("imei")} cursor="pointer">IMEI {sortBy==="imei" && (<Icon as={sortOrder==="ascending"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "imei" },
      { Header: "IMEI 2", accessor: "imei2" },
      { Header: () => (<Flex align="center" onClick={() => handleHeaderSort("status")} cursor="pointer">Status {sortBy==="status" && (<Icon as={sortOrder==="ascending"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "status" },
      { Header: () => (<Flex align="center" onClick={() => handleHeaderSort("firmwareVersion")} cursor="pointer">Firmware {sortBy==="firmwareVersion" && (<Icon as={sortOrder==="ascending"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "firmwareVersion" },
      { Header: () => (<Flex align="center" onClick={() => handleHeaderSort("agentVersion")} cursor="pointer">Agent Version {sortBy==="agentVersion" && (<Icon as={sortOrder==="ascending"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "agentVersion" },
      { Header: () => (<Flex align="center" onClick={() => handleHeaderSort("createDate")} cursor="pointer">Created {sortBy==="createDate" && (<Icon as={sortOrder==="ascending"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "createDate" },
      { Header: () => (<Flex align="center" onClick={() => handleHeaderSort("modifiedDate")} cursor="pointer">Modified {sortBy==="modifiedDate" && (<Icon as={sortOrder==="ascending"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "modifiedDate" },
      { Header: () => (<Flex align="center" onClick={() => handleHeaderSort("licenseExpiryDate")} cursor="pointer">License Expiry {sortBy==="licenseExpiryDate" && (<Icon as={sortOrder==="ascending"?ChevronUpIcon:ChevronDownIcon} ml={1} />)}</Flex>), accessor: "licenseExpiryDate" },
    ],
    [sortBy, sortOrder]
  );

  const data = useMemo(() => {
    return items.map((d) => ({
      imei: displayOrDash(d?.imei),
      imei2: displayOrDash(d?.imei2),
      status: displayOrDash(d?.status),
      firmwareVersion: displayOrDash(d?.firmwareVersion),
      agentVersion: displayOrDash(d?.agentVersion),
      createDate: formatEpochDate(d?.createDate),
      modifiedDate: formatEpochDate(d?.modifiedDate),
      licenseExpiryDate: formatEpochDate(d?.licenseExpiryDate),
    }));
  }, [items]);

  return (
    <Box
      position="fixed"
      top="80px"
      left={{ base: 0, xl: sidebarCollapsed ? "80px" : "290px" }}
      right="0"
      bottom="0"
      display="flex"
      flexDirection="column"
      p={{ base: "20px", md: "30px" }}
      pe="20px"
      overflow="hidden"
    >
      <Box mb={3} flexShrink={0}>
        <Flex justifyContent="flex-end" mb={3}>
          <FilterSystem
            filterFields={filterFields}
            onFilterChange={(newFilters) => {
              setFilters(newFilters);
              setPage(1);
            }}
            initialFilters={filters}
            externalFilters={filters}
            showTags={false}
          />
        </Flex>
        {getActiveFilterCount(filters) > 0 && (
          <Wrap spacing={2}>
            {filterFields.map((field) => {
              if (field.type === "daterange") {
                const fromValue = filters[`${field.key}_from`];
                const toValue = filters[`${field.key}_to`];
                if (fromValue || toValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {fromValue || "..."} to {toValue || "..."}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(`${field.key}_from`, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              } else {
                const value = filters[field.key];
                const hasValue = field.type === "multiselect"
                  ? Array.isArray(value) && value.length > 0
                  : value;

                if (hasValue) {
                  return (
                    <WrapItem key={field.key}>
                      <Tag
                        size="md"
                        borderRadius="full"
                        variant="solid"
                        bg={useColorModeValue("blue.50", "blue.900")}
                        color={useColorModeValue("blue.700", "blue.200")}
                      >
                        <TagLabel>
                          {field.label}: {getFilterDisplayValue(field, value)}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeFilterHelper(field.key, filters, setFilters, (newFilters) => {
                          setFilters(newFilters);
                          setPage(1);
                        }, filterFields)} />
                      </Tag>
                    </WrapItem>
                  );
                }
              }
              return null;
            })}
          </Wrap>
        )}
      </Box>
      <Box flex="1" minH="0">
        <MainTable
          loaded={!isKnoxDevicesLoading}
          data={data}
          columns={columns}
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={page}
          perPage={perPage}
          perPageOptions={[50, 100, 200]}
          exports={data}
          totalPage={totalPages}
          name={`Knox Devices (${totalCount})`}
          containerHeight="100%"
        />
      </Box>
    </Box>
  );
}

export default KnoxDevices;
